import assert from 'assert-ts';
import uniqueId from 'lodash/uniqueId';
import type {Locale} from 'localization/types';
import {translate} from 'localization';
import type {Schemas} from 'api/dto.generated';
import type {
  Thesaurus,
  ThesaurusId,
  ThesaurusNode,
  ThesaurusNodeDto,
} from '../types';
import {modifyNeoThesaurus} from './modifyNeoThesaurus';
import {modifyThemaThesaurus} from './modifyThemaThesaurus';

export const mapThesaurusDto = (
  nodeDtos: ThesaurusNodeDto[],
  thesaurusId: ThesaurusId,
  language: Locale,
): Thesaurus => {
  const thesaurus: Thesaurus = {
    id: thesaurusId,
    code: thesaurusId,
    label: translate(`thesaurus.${thesaurusId}.root.label`),
    language,
    children: nodeDtos.map(dto => mapThesaurusNodeDto(thesaurusId, dto)),
  };

  // Wraps letter categories into a new top-level category
  if (thesaurus.id === 'thema') {
    return modifyThemaThesaurus(thesaurus);
  }

  if (thesaurus.id === 'neo') {
    return modifyNeoThesaurus(thesaurus);
  }

  return thesaurus;
};

export const mapThesaurusNodeDto = (
  thesaurusId: ThesaurusId,
  dto: ThesaurusNodeDto,
): ThesaurusNode => {
  const children = (dto.children ?? []).map(c => {
    const cc = {
      ...c,
      deactivated:
        (c as ThesaurusNodeDto).deactivated ||
        (c as ThesaurusNodeDto).deprecated,
    } as ThesaurusNodeDto;

    return mapThesaurusNodeDto(thesaurusId, cc);
  });
  const anyActiveChildren = children.some(c => !c.deactivated);

  // TEMP while backend fix duplicate id; If grep, create id.
  const id =
    thesaurusId === 'grep'
      ? uniqueId(dto.id)
      : assert(dto.id, 'mapThesaurusNodeDto: id missing', dto);

  // GenreAndForm and NEO sends uri instead of code
  const code =
    (thesaurusId === 'genreandform' || thesaurusId === 'neo'
      ? dto.uri
      : dto.code) ?? id;

  return {
    id: id,
    code,
    label: assert(dto.label, 'mapThesaurusNodeDto: label missing', dto),
    deactivated: (dto.deactivated || dto.deprecated) && !anyActiveChildren,
    selectable: !dto.deactivated && !dto.deprecated,
    type: dto.type,
    alternativeLabels: dto.alternativeLabels ?? [],
    related: dto.related
      ? dto.related
          // Exclude deactivated relations
          .filter(r => !(r as Schemas.ThesaurusConcept).deactivated)
          .map(mapThesaurusConceptDto)
      : undefined,
    note: dto.note,
    definition: dto.definition,
    children,
  };
};

const mapThesaurusConceptDto = (
  dto:
    | Schemas.ThesaurusConcept
    | Schemas.GenreAndFormConcept
    | Schemas.ThemaConcept,
): ThesaurusNode => ({
  ...dto,
  id: assert(dto.id, 'mapThesaurusConceptDto: id missing', dto),
  code: dto.id,
  label: assert(dto.label, 'mapThesaurusConceptDto: label missing', dto),
  children: [],
});
